// src/context/AuthContext.js - Complete Enhanced Version with Privacy Modal Fix
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { authService } from '../services/auth';
import api from '../services/api';
import logger from '../utils/logger';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Initialize state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem('sessionId') || null;
  });
  const [clientStatus, setClientStatus] = useState(() => {
    const savedStatus = localStorage.getItem('clientStatus');
    return savedStatus || 'unknown';
  });

  // Add flags to prevent multiple simultaneous operations
  const refreshingStatus = useRef(false);
  const statusRefreshTimer = useRef(null);
  const lastRefreshTime = useRef(0);
  const profileRetryTimer = useRef(null);

  // Helper function to store client status
  const storeClientStatus = (status) => {
    if (status) {
      localStorage.setItem('clientStatus', status);
      logger.info(`Stored client status in localStorage: ${status}`);
    }
  };

  // Helper function to manage session
  const updateSession = (newSessionId) => {
    if (newSessionId) {
      setSessionId(newSessionId);
      localStorage.setItem('sessionId', newSessionId);
      api.defaults.headers.common['X-Session-Id'] = newSessionId;
      logger.info('Session updated', { sessionId: newSessionId });
    }
  };

  const clearSession = () => {
    setSessionId(null);
    localStorage.removeItem('sessionId');
    delete api.defaults.headers.common['X-Session-Id'];
    logger.info('Session cleared');
  };

  // ENHANCED: Robust profile fetching with retry mechanism
  const fetchUserProfile = async (retries = 3, delayMs = 1000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        logger.info(`Fetching user profile (attempt ${attempt}/${retries})`);

        const profileResponse = await api.get('/users/profile');

        if (profileResponse.data && profileResponse.data.success) {
          const profileData = profileResponse.data.data;

          // Validate that we have the essential privacy fields
          const hasPrivacyField = profileData.hasOwnProperty('privacyPolicyAccepted');
          const hasTermsField = profileData.hasOwnProperty('termsOfServiceAccepted');

          logger.info('User profile fetched successfully:', {
            userId: profileData.id,
            email: profileData.email,
            privacyPolicyAccepted: profileData.privacyPolicyAccepted,
            termsOfServiceAccepted: profileData.termsOfServiceAccepted,
            hasPrivacyField,
            hasTermsField,
            privacyType: typeof profileData.privacyPolicyAccepted,
            termsType: typeof profileData.termsOfServiceAccepted
          });

          // Ensure privacy fields are properly boolean or null, not undefined
          const cleanedProfileData = {
            ...profileData,
            privacyPolicyAccepted: hasPrivacyField ? !!profileData.privacyPolicyAccepted : null,
            termsOfServiceAccepted: hasTermsField ? !!profileData.termsOfServiceAccepted : null
          };

          return cleanedProfileData;
        } else {
          throw new Error(`Profile response not successful: ${JSON.stringify(profileResponse.data)}`);
        }
      } catch (error) {
        logger.error(`Profile fetch attempt ${attempt} failed:`, {
          error: error.message,
          status: error.response?.status,
          data: error.response?.data
        });

        if (attempt === retries) {
          throw error; // Last attempt, throw the error
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      }
    }
  };

  // Enhanced function to update user privacy acceptance
  const updateUserPrivacyAcceptance = (privacyAccepted, termsAccepted) => {
    logger.info('Updating user privacy acceptance in context', {
      privacyAccepted,
      termsAccepted,
      currentUserId: user?.id
    });

    setUser(prevUser => {
      if (!prevUser) return prevUser;

      const updatedUser = {
        ...prevUser,
        privacyPolicyAccepted: privacyAccepted,
        termsOfServiceAccepted: termsAccepted,
        privacyPolicyAcceptedAt: privacyAccepted ? new Date().toISOString() : prevUser.privacyPolicyAcceptedAt,
        termsOfServiceAcceptedAt: termsAccepted ? new Date().toISOString() : prevUser.termsOfServiceAcceptedAt,
        profileLoadError: false // Clear any previous profile errors
      };

      logger.info('User privacy acceptance updated in context:', {
        userId: updatedUser.id,
        privacyPolicyAccepted: updatedUser.privacyPolicyAccepted,
        termsOfServiceAccepted: updatedUser.termsOfServiceAccepted
      });

      return updatedUser;
    });
  };

  // Enhanced manual profile refresh function
  const refreshUserProfile = async () => {
    if (!user?.id) {
      logger.warn('Cannot refresh profile - no user ID');
      return;
    }

    try {
      logger.info('Manually refreshing user profile...');
      const profileData = await fetchUserProfile();

      setUser(prevUser => {
        const updatedUser = {
          ...prevUser,
          ...profileData,
          profileLoadError: false
        };

        logger.info('Manual profile refresh successful:', {
          userId: updatedUser.id,
          privacyPolicyAccepted: updatedUser.privacyPolicyAccepted,
          termsOfServiceAccepted: updatedUser.termsOfServiceAccepted
        });

        return updatedUser;
      });

    } catch (error) {
      logger.error('Manual profile refresh failed:', error);
      throw error;
    }
  };

  // Function to check if profile is complete
  const isProfileComplete = (userData) => {
    if (!userData) return false;

    const hasPrivacyField = userData.hasOwnProperty('privacyPolicyAccepted');
    const hasTermsField = userData.hasOwnProperty('termsOfServiceAccepted');

    // Profile is complete if both fields exist (even if they're false)
    return hasPrivacyField && hasTermsField;
  };

  // Enhanced authentication check on app load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const auth = authService.isAuthenticated();

        if (auth) {
          // Get basic user info from token first
          const tokenData = authService.getUserFromToken();

          logger.info('Token data loaded:', {
            userId: tokenData.id,
            email: tokenData.email,
            clientId: tokenData.clientId,
            role: tokenData.role
          });

          // Store the user ID in localStorage for validation checks
          if (tokenData && tokenData.id) {
            localStorage.setItem('userId', tokenData.id);
          }

          // CRITICAL FIX: Always try to get complete profile from database
          let userData = null;
          try {
            const profileData = await fetchUserProfile();

            // Merge token data with profile data (profile takes precedence)
            userData = {
              ...tokenData,
              ...profileData,
              token: localStorage.getItem('token'),
              twoFactorEnabled: profileData.twoFactorEnabled || false,
              profileLoadError: false
            };

            logger.info('Complete user profile loaded successfully:', {
              userId: userData.id,
              email: userData.email,
              privacyPolicyAccepted: userData.privacyPolicyAccepted,
              termsOfServiceAccepted: userData.termsOfServiceAccepted,
              profileComplete: isProfileComplete(userData)
            });

          } catch (profileError) {
            logger.error('Failed to load user profile during initial auth:', profileError);

            // CRITICAL: Set user with explicit incomplete profile markers
            userData = {
              ...tokenData,
              token: localStorage.getItem('token'),
              twoFactorEnabled: tokenData.twoFactorEnabled || false,
              privacyPolicyAccepted: null, // Explicitly set to null to indicate unknown
              termsOfServiceAccepted: null, // Explicitly set to null to indicate unknown
              profileLoadError: true
            };

            // Schedule a retry after a delay
            profileRetryTimer.current = setTimeout(async () => {
              logger.info('Retrying profile fetch after initial failure...');
              try {
                await refreshUserProfile();
              } catch (retryError) {
                logger.error('Profile retry also failed:', retryError);
                // Final fallback - assume user needs to accept policies
                setUser(prevUser => ({
                  ...prevUser,
                  privacyPolicyAccepted: false,
                  termsOfServiceAccepted: false,
                  profileLoadError: true
                }));
              }
            }, 3000);
          }

          setUser(userData);

          // Handle session management
          const existingSessionId = localStorage.getItem('sessionId');
          if (existingSessionId) {
            updateSession(existingSessionId);

            // Verify session is still valid
            try {
              const response = await api.get('/auth/session-status');
              if (response.data?.data?.hasSession) {
                logger.info('Existing session validated', { sessionId: existingSessionId });
              } else {
                logger.warn('Existing session invalid, will create new on next request');
                clearSession();
              }
            } catch (error) {
              logger.warn('Could not verify session status', error);
            }
          }

          // Set admin status
          const isUserAdmin = tokenData.role === 'admin';
          setIsAdmin(isUserAdmin);

          // Handle client status
          if (isUserAdmin) {
            const adminStatus = 'active';
            setClientStatus(adminStatus);
            storeClientStatus(adminStatus);
          } else if (tokenData.clientId) {
            const activeClientId = localStorage.getItem('activeClientId');

            if (activeClientId) {
              setClientStatus('active');
              storeClientStatus('active');
              logger.info(`Using stored active client: ${activeClientId}`);
            } else {
              const storedStatus = localStorage.getItem('clientStatus');
              if (storedStatus) {
                setClientStatus(storedStatus);
                logger.info(`Using stored client status: ${storedStatus}`);
              } else {
                setClientStatus('pending');
                if (statusRefreshTimer.current) {
                  clearTimeout(statusRefreshTimer.current);
                }
                statusRefreshTimer.current = setTimeout(() => {
                  refreshClientStatus();
                }, 100);
              }
            }
          }
        }

        setIsAuthenticated(auth);
      } catch (error) {
        logger.error('Authentication check failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Cleanup timers on unmount
    return () => {
      if (statusRefreshTimer.current) {
        clearTimeout(statusRefreshTimer.current);
      }
      if (profileRetryTimer.current) {
        clearTimeout(profileRetryTimer.current);
      }
    };
  }, []);

  // Enhanced profile completeness monitoring
  useEffect(() => {
    const monitorProfileCompleteness = async () => {
      // Only monitor if user exists and auth is not loading
      if (!user?.id || isLoading) return;

      // If profile is incomplete and we haven't recently tried to fix it
      if (!isProfileComplete(user) && !profileRetryTimer.current) {
        logger.info('Profile appears incomplete, scheduling retry...', {
          userId: user.id,
          hasPrivacyField: user.hasOwnProperty('privacyPolicyAccepted'),
          hasTermsField: user.hasOwnProperty('termsOfServiceAccepted'),
          privacyValue: user.privacyPolicyAccepted,
          termsValue: user.termsOfServiceAccepted
        });

        profileRetryTimer.current = setTimeout(async () => {
          try {
            await refreshUserProfile();
          } catch (error) {
            logger.error('Scheduled profile refresh failed:', error);
          } finally {
            profileRetryTimer.current = null;
          }
        }, 2000);
      }
    };

    monitorProfileCompleteness();

    // Cleanup timer when user changes
    return () => {
      if (profileRetryTimer.current) {
        clearTimeout(profileRetryTimer.current);
        profileRetryTimer.current = null;
      }
    };
  }, [user?.id, isLoading]);

  // Client status refresh function (unchanged from your original)
  const refreshClientStatus = async () => {
    if (!user || !user.clientId) return;

    // Admin users always have 'active' status
    if (user.role === 'admin') {
      const adminStatus = 'active';
      setClientStatus(adminStatus);
      storeClientStatus(adminStatus);
      return;
    }

    // Prevent multiple simultaneous refreshes
    if (refreshingStatus.current) {
      logger.info('Status refresh already in progress, skipping');
      return;
    }

    // Enforce a minimum interval between refreshes
    const now = Date.now();
    if (now - lastRefreshTime.current < 5000) {
      logger.info('Rate limiting status refresh, too many requests');
      return;
    }

    try {
      refreshingStatus.current = true;
      lastRefreshTime.current = now;

      logger.info('Fetching all clients for user');
      const userClientsResponse = await api.get('/clients/user-client');

      if (userClientsResponse.data && userClientsResponse.data.success) {
        const clients = userClientsResponse.data.data;
        logger.info(`Found ${clients.length} clients for user`);

        const activeClient = clients.find(client => client.status === 'active');

        if (activeClient) {
          logger.info(`Found active client: ${activeClient.clientId}`);
          localStorage.setItem('activeClientId', activeClient.clientId);
          setClientStatus('active');
          storeClientStatus('active');
          return;
        }
      }

      // Fallback to checking the specific client
      logger.info(`Checking specific client status: ${user.clientId}`);
      const response = await api.get(`/clients/status/${user.clientId}`);

      if (response.data && response.data.success) {
        const clientData = response.data.data;
        const newStatus = clientData.status;

        logger.info(`Client status from API: ${newStatus} for client ${clientData.clientId}`);

        if (newStatus === 'active') {
          localStorage.setItem('activeClientId', clientData.clientId);
        }

        setClientStatus(newStatus);
        storeClientStatus(newStatus);
      }
    } catch (error) {
      logger.error('Failed to refresh client status:', error);
      const activeClientId = localStorage.getItem('activeClientId');
      if (activeClientId) {
        logger.info(`Using stored active client after error: ${activeClientId}`);
        setClientStatus('active');
        storeClientStatus('active');
      } else {
        logger.info('Setting fallback active status for client');
        setClientStatus('active');
        storeClientStatus('active');
        if (user.clientId) {
          localStorage.setItem('activeClientId', user.clientId);
        }
      }
    } finally {
      refreshingStatus.current = false;
    }
  };

  // Enhanced login function with proper profile fetching
  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const isEmailLogin = credentials.email && credentials.password;
      const isClientIdLogin = credentials.clientId && credentials.clientSecret;

      if (!isEmailLogin && !isClientIdLogin) {
        throw new Error('Invalid credentials format');
      }

      const loginResult = await authService.login(credentials);

      // Handle 2FA requirement
      if (loginResult.requireTwoFactor) {
        logger.info('2FA verification required');
        setIsLoading(false);
        return {
          requireTwoFactor: true,
          userId: loginResult.userId,
          email: loginResult.email,
          clientId: loginResult.clientId
        };
      }

      // Handle token generation requirement
      if (loginResult.requiresTokenGeneration) {
        logger.info('First-time login - token generation required');
        setIsLoading(false);
        return {
          requiresTokenGeneration: true,
          token: loginResult.token
        };
      }

      // Handle session
      if (loginResult.sessionId) {
        updateSession(loginResult.sessionId);
      }

      setIsAuthenticated(true);

      // ENHANCED: Always fetch complete profile after login
      const userData = authService.getUserFromToken();
      if (userData && userData.id) {
        localStorage.setItem('userId', userData.id);
      }

      let completeUserInfo = null;
      try {
        const profileData = await fetchUserProfile();

        completeUserInfo = {
          ...userData,
          ...profileData, // Profile data takes precedence
          token: loginResult.accessToken,
          email: isEmailLogin ? credentials.email : userData.email,
          twoFactorEnabled: profileData.twoFactorEnabled || userData.twoFactorEnabled || false,
          profileLoadError: false
        };

        logger.info('Login successful with complete profile:', {
          userId: completeUserInfo.id,
          email: completeUserInfo.email,
          privacyPolicyAccepted: completeUserInfo.privacyPolicyAccepted,
          termsOfServiceAccepted: completeUserInfo.termsOfServiceAccepted
        });
      } catch (profileError) {
        logger.error('Profile fetch failed during login:', profileError);

        // Set incomplete user data with explicit markers
        completeUserInfo = {
          ...userData,
          token: loginResult.accessToken,
          email: isEmailLogin ? credentials.email : userData.email,
          twoFactorEnabled: userData.twoFactorEnabled || false,
          privacyPolicyAccepted: null,
          termsOfServiceAccepted: null,
          profileLoadError: true
        };

        // Schedule profile retry
        setTimeout(async () => {
          try {
            await refreshUserProfile();
          } catch (retryError) {
            logger.error('Login profile retry failed:', retryError);
          }
        }, 2000);
      }

      setUser(completeUserInfo);
      setIsAdmin(userData.role === 'admin');

      // Handle client status
      if (userData.clientId) {
        const newStatus = loginResult.clientStatus || (isClientIdLogin ? 'active' : 'pending');
        setClientStatus(newStatus);
        storeClientStatus(newStatus);

        if (newStatus === 'active' || isClientIdLogin) {
          localStorage.setItem('activeClientId', userData.clientId);
        }

        setTimeout(() => {
          refreshClientStatus();
        }, 500);
      }

      setIsLoading(false);
      return loginResult;
    } catch (error) {
      logger.error('Login failed:', error);
      setIsLoading(false);
      throw error;
    }
  };

  // Enhanced 2FA verification with profile fetching
  const verify2FA = async (userId, token) => {
    try {
      const result = await authService.verify2FA(userId, token);

      if (result.sessionId) {
        updateSession(result.sessionId);
      }

      const userData = authService.getUserFromToken();

      // Fetch complete profile after 2FA
      try {
        const profileData = await fetchUserProfile();

        setUser({
          ...userData,
          ...profileData,
          token: result.accessToken,
          twoFactorEnabled: true,
          profileLoadError: false
        });
      } catch (profileError) {
        logger.error('Profile fetch failed after 2FA:', profileError);

        setUser({
          ...userData,
          token: result.accessToken,
          twoFactorEnabled: true,
          privacyPolicyAccepted: null,
          termsOfServiceAccepted: null,
          profileLoadError: true
        });
      }

      setIsAuthenticated(true);
      setIsAdmin(userData.role === 'admin');

      logger.info('2FA verification successful', {
        userId: userData.id,
        sessionId: result.sessionId
      });

      return result;
    } catch (error) {
      logger.error('2FA verification failed:', error);
      throw error;
    }
  };

  // Registration function (unchanged from your original)
  const register = async (userData) => {
    setIsLoading(true);
    try {
      if (!userData.clientName || !userData.email || !userData.password) {
        throw new Error('Client name, email and password are required');
      }

      const result = await authService.register(userData);

      if (result.data?.sessionId) {
        updateSession(result.data.sessionId);
      }

      setIsLoading(false);
      return result;
    } catch (error) {
      logger.error('Registration failed:', error);
      setIsLoading(false);
      throw error;
    }
  };

  // Enhanced logout function
  const logout = async () => {
    try {
      const currentUserId = user?.id;
      const currentSessionId = sessionId;

      // Clear any client-side financial data
      sessionStorage.removeItem('plaidConnected');
      sessionStorage.removeItem('plaidAccounts');
      localStorage.removeItem('financialData');
      localStorage.removeItem('clientStatus');
      localStorage.removeItem('activeClientId');

      // Clear any pending timers
      if (statusRefreshTimer.current) {
        clearTimeout(statusRefreshTimer.current);
        statusRefreshTimer.current = null;
      }
      if (profileRetryTimer.current) {
        clearTimeout(profileRetryTimer.current);
        profileRetryTimer.current = null;
      }

      // Notify the backend
      if (currentUserId || currentSessionId) {
        try {
          await api.post('/auth/logout', {
            refreshToken: localStorage.getItem('refreshToken'),
            sessionId: currentSessionId
          });
          logger.info(`Server logout successful for user ${currentUserId}`);
        } catch (apiError) {
          logger.error('Failed to notify server about logout:', apiError);
        }
      }

      // Clear session and state
      clearSession();
      authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setIsAdmin(false);
      setClientStatus('unknown');

      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');

      logger.info('User logged out successfully');
    } catch (error) {
      logger.error('Error during logout:', error);

      // Still clear state on error
      clearSession();
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('clientStatus');
      localStorage.removeItem('activeClientId');
    }
  };

  // Update token function (enhanced)
  const updateToken = (token) => {
    if (token) {
      localStorage.setItem('token', token);
      const userData = authService.getUserFromToken();

      // Merge with existing user data to preserve profile information
      setUser(prevUser => ({
        ...prevUser,
        ...userData,
        token
      }));

      if (userData && userData.id) {
        localStorage.setItem('userId', userData.id);
      }

      setIsAdmin(userData.role === 'admin');

      if (userData.role !== 'admin' && userData.clientId) {
        if (statusRefreshTimer.current) {
          clearTimeout(statusRefreshTimer.current);
        }
        statusRefreshTimer.current = setTimeout(() => {
          refreshClientStatus();
        }, 500);
      }
    }
  };

  // Update 2FA status function (unchanged)
  const updateUser2FAStatus = (enabled) => {
    if (user) {
      setUser({
        ...user,
        twoFactorEnabled: enabled
      });
      logger.info(`Updated user 2FA status to ${enabled ? 'enabled' : 'disabled'}`);
    }
  };

  // Clear conversation history function (unchanged)
  const clearConversationHistory = async () => {
    try {
      if (!sessionId) {
        logger.warn('No session to clear');
        return;
      }

      const response = await api.delete('/insights/session', {
        data: { sessionId }
      });

      if (response.data?.data?.sessionId) {
        updateSession(response.data.data.sessionId);
        logger.info('Conversation history cleared, new session created', {
          newSessionId: response.data.data.sessionId
        });
      }

      return response.data;
    } catch (error) {
      logger.error('Failed to clear conversation history', error);
      throw error;
    }
  };

  // Update auth function (enhanced)
  const updateAuth = () => {
    const token = localStorage.getItem('token');
    const storedSessionId = localStorage.getItem('sessionId');

    if (token) {
      setIsAuthenticated(true);
      const userData = authService.getUserFromToken();

      // Preserve existing user data when updating auth
      setUser(prevUser => ({
        ...prevUser,
        ...userData,
        token
      }));

      if (userData && userData.id) {
        localStorage.setItem('userId', userData.id);
      }

      if (storedSessionId) {
        updateSession(storedSessionId);
      }

      setIsAdmin(userData.role === 'admin');

      if (userData.role === 'admin') {
        setClientStatus('active');
        storeClientStatus('active');
      } else if (userData.clientId) {
        const activeClientId = localStorage.getItem('activeClientId');
        if (activeClientId) {
          setClientStatus('active');
          storeClientStatus('active');
        } else {
          const savedStatus = localStorage.getItem('clientStatus');
          const newStatus = savedStatus || 'active';
          setClientStatus(newStatus);
          storeClientStatus(newStatus);

          if (newStatus === 'active') {
            localStorage.setItem('activeClientId', userData.clientId);
          }

          if (statusRefreshTimer.current) {
            clearTimeout(statusRefreshTimer.current);
          }
          statusRefreshTimer.current = setTimeout(() => {
            refreshClientStatus();
          }, 500);
        }
      }
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    isAdmin,
    clientStatus,
    sessionId,
    hasValidSession: () => !!sessionId,
    refreshClientStatus,
    refreshUserProfile, // Enhanced profile refresh
    isProfileComplete: (userData) => isProfileComplete(userData || user), // Profile completeness check
    login,
    verify2FA,
    register,
    logout,
    updateToken,
    updateUser2FAStatus,
    updateAuth,
    clearConversationHistory,
    updateUserPrivacyAcceptance
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;